<script setup lang="ts">
import { Button } from '@dashhudson/dashing-ui/button';
import { ChatAgentSidePanel } from '@dashhudson/dashing-ui/chat-agent-side-panel';
import { ChatBubble, ChatBubbleFooter, ChatBubbleHeader } from '@dashhudson/dashing-ui/chat-bubble';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import type { LikeDislikeValue } from '@dashhudson/dashing-ui/like-dislike-buttons';
import { Menu } from '@dashhudson/dashing-ui/menu';
import type { CannedPrompt } from '@dashhudson/dashing-ui/prompt-box';
import { useSidePanel, useSidePanelLifecycle } from '@dashhudson/dashing-ui/side-panel';
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { VisionAiChip } from '@dashhudson/dashing-ui/vision-ai-chip';
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import AssistantMessageSidePanelContent from '@/components/VisionAi/chat-panel-components/AssistantMessageSidePanelContent.vue';
import { useProgressiveLoading } from '@/components/VisionAi/chat-panel-components/composables/useProgressiveLoading';
import { useSubmitUserPrompt } from '@/components/VisionAi/chat-panel-components/composables/useSubmitUserPrompt';
import { unanchoredWidgets } from '@/components/VisionAi/chat-panel-components/utils/unanchoredWidgets';
import {
  type ChatMessage,
  ChatMessageRole,
  type VisionAiStoreMessage,
} from '@/components/VisionAi/chat-panel-components/utils/VisionChatSidePanel.types';
import VisionAiChatButton from '@/components/VisionAi/chat-panel-components/VisionAiChatButton.vue';
import VisionChatHistory from '@/components/VisionAi/chat-panel-components/VisionChatHistory.vue';
import WidgetWrapper from '@/components/VisionAi/chat-panel-components/widgets/WidgetWrapper.vue';
import { useClearChatHistoryConfirmation } from '@/components/VisionAi/composables/useClearChatHistoryConfirmation';
import { useNewChatDisabled } from '@/components/VisionAi/composables/useNewChatDisabled';
import {
  useSubmitPromptConfig,
  type VisionPromptConfig,
} from '@/components/VisionAi/composables/useSubmitPromptConfig';
import {
  DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE,
  INTENT_TYPES,
  SUMMARY_INTENT_TYPES,
  USER_FEEDBACK,
  VISION_AI_PANEL_TYPE,
  VISION_AI_SUMMARY_INTERACTIONS,
  VISION_PANEL_WIDTH_EXPANDED,
} from '@/components/VisionAi/constants';
import { getIntentQuery } from '@/components/VisionAi/utils/intent_query';
import {
  aiMessageInteractionEvent,
  aiUserFeedbackEvent,
} from '@/components/VisionAi/utils/mixpanel-events';
import { buildRequestContext, usePageFilters } from '@/components/VisionAi/utils/request-context';
import { VISION_AI_SENDER } from '@/config';
import { useIdentityStore } from '@/stores/identity';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';
import type { Identity } from '@/types/auth.types';
import { stripHtmlTags } from '@/utils/text-utils';
import { formatDate, formatTimestamp } from '@/utils/vision';

const identityStore = useIdentityStore();
const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();
const visionAiMessagesStore = useVisionAiMessagesStore();
const route = useRoute();
const pageFilters = usePageFilters();
const { showClearChatHistoryConfirmation } = useClearChatHistoryConfirmation();
const { addToast: addFeedbackToast } = useToast({
  severity: 'info',
  message: 'Thank you for the feedback',
  life: 5000,
});
const { addToast: addRegenerateErrorToast } = useToast({
  severity: 'error',
  message: 'Failed to regenerate message. Please try again.',
  life: 5000,
});

const userName = computed(() => (identityStore.identity as Identity | null)?.first_name);
const userAvatarUrl = computed(() => (identityStore.identity as Identity | null)?.avatar_url);

const { open, close, type, visible, expanded, setExpanded } = useSidePanel();
const promptValue = ref('');

// While a drawer is open the panel collapses without touching the persisted
// preference; toggle clicks flip `transientExpanded` instead. When the drawer
// closes the transient drops and the preference resumes.
const { visible: drawerVisible } = useDrawer();
const transientExpanded = ref<boolean | null>(null);

const expandedOverride = computed<boolean | null>(() =>
  drawerVisible.value ? (transientExpanded.value ?? false) : null,
);

const isPanelExpanded = computed(() =>
  expandedOverride.value !== null ? expandedOverride.value : expanded.value,
);

function handleExpandRequest() {
  if (drawerVisible.value) {
    transientExpanded.value = true;
  } else {
    setExpanded(true);
  }
}

watch(drawerVisible, (now, prev) => {
  if (prev && !now) transientExpanded.value = null;
});

useSidePanelLifecycle({
  onBeforeClose: (reason, closeType) => {
    if (
      closeType === VISION_AI_PANEL_TYPE &&
      reason === 'programmatic' &&
      visionAiStore.keepVisionPanelOpen
    ) {
      return false;
    }
    return undefined;
  },
  onToggleExpanded: (toggleType) => {
    if (toggleType !== VISION_AI_PANEL_TYPE) return true;
    if (drawerVisible.value) {
      transientExpanded.value = !(transientExpanded.value ?? false);
      return false;
    }
    return true;
  },
});

// Sync chat-panel-open state into the vision-ai store so that the task-completion
// toast trigger (in vision-ai-messages) can check it without calling dashing-ui
// composables from inside a store.
watch(
  [visible, type],
  ([isVisible, panelType]) => {
    const isVisionVisible = isVisible && panelType === VISION_AI_PANEL_TYPE;
    visionAiStore.setVisionChatPanelOpen(isVisionVisible);
    if (!isVisionVisible) {
      visionAiStore.expectingNewMessage = false;
    }
  },
  { immediate: true },
);

const { newChatDisabled, newChatTooltip } = useNewChatDisabled();

const isLoading = computed(() => visionAiStore.loadingMessageInCurrentConversation);

const { messagesWithLoading } = useProgressiveLoading(
  computed(() => visionAiMessagesStore.messages),
  isLoading,
);

// Bind the TransitionGroup remount key to the conversation that actually owns the
// currently-rendered messages. Using currentConversationId remounted on click —
// before the new messages had loaded — which produced an extra appear cycle on
// stale data and a visible empty frame between the two animations.
const loadedConversationId = computed(() => {
  const first = (visionAiMessagesStore.messages as VisionAiStoreMessage[])[0];
  return first?.conversationId ?? null;
});

const { handleSubmit } = useSubmitUserPrompt(promptValue);
const { submitPromptConfig } = useSubmitPromptConfig();

type SidePanelCannedPrompt = CannedPrompt & { visionPromptConfig?: VisionPromptConfig };

async function handlePromptInsert(prompt: SidePanelCannedPrompt) {
  if (prompt.visionPromptConfig) {
    await submitPromptConfig(prompt.visionPromptConfig);
  }
}

function getLikeDislikeValue(feedback?: string | null): LikeDislikeValue {
  if (feedback === USER_FEEDBACK.POSITIVE) return 'like';
  if (feedback === USER_FEEDBACK.NEGATIVE) return 'dislike';
  return null;
}

function getFeedbackValue(likeDislike: LikeDislikeValue) {
  if (likeDislike === 'like') return USER_FEEDBACK.POSITIVE;
  if (likeDislike === 'dislike') return USER_FEEDBACK.NEGATIVE;
  return null;
}

function getStoreMessage(messageId: string | number) {
  return visionAiMessagesStore.messages.find((message) => message.id === messageId);
}

function trackAssistantMessageInteraction(
  interaction: string,
  message: Pick<ChatMessage, 'id' | 'intentType'>,
) {
  aiMessageInteractionEvent({
    interaction,
    intentType: message.intentType,
    messageId: message.id,
  });
}

function handleCopyResponse(message: ChatMessage) {
  trackAssistantMessageInteraction(VISION_AI_SUMMARY_INTERACTIONS.COPY, message);
  visionAiStore.copyResponse(message.text);
}

async function handleFeedback(message: ChatMessage, likeDislike: LikeDislikeValue) {
  const feedback = getFeedbackValue(likeDislike);
  await visionAiMessagesStore.updateMessage({ messageId: message.id, feedback });

  if (!feedback) return;
  addFeedbackToast();

  const storeMessage = getStoreMessage(message.id);
  if (storeMessage?.additionalContent) {
    aiUserFeedbackEvent({
      feedback:
        feedback === USER_FEEDBACK.POSITIVE
          ? VISION_AI_SUMMARY_INTERACTIONS.THUMBS_UP
          : VISION_AI_SUMMARY_INTERACTIONS.THUMBS_DOWN,
      message: storeMessage,
    });
  }
}

async function handleRegenerateSummaryMessage(assistantMessage: VisionAiStoreMessage) {
  if (!assistantMessage.conversationId || !assistantMessage.intentType) return;

  const userPrompt = DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE[assistantMessage.intentType];
  if (!userPrompt) return;

  const currentBrandId = identityStore.currentBrand?.id ?? 0;
  const intentFilters = assistantMessage.intentQuery ?? getIntentQuery(assistantMessage.intentType);
  const filters = { ...intentFilters, ...pageFilters.value };
  const newUserMsg = await visionAiMessagesStore.createMessage({
    conversationId: assistantMessage.conversationId,
    intentType: assistantMessage.intentType,
    intentQuery: assistantMessage.intentQuery,
    sender: VISION_AI_SENDER.USER,
    message: userPrompt,
    requestContext: buildRequestContext(route, currentBrandId, filters),
  });

  if (!visionAiStore.visionAiChatWindowOpen && newUserMsg) {
    visionAiMessagesStore.addMessageToChatWindow(newUserMsg);
  }
}

async function handleRegenerateMessage(message: ChatMessage) {
  const assistantMessage = await visionAiMessagesStore.getMessageById(message.id, {
    includeDeleted: false,
  });
  if (!assistantMessage?.conversationId) return;

  visionAiMessagesStore.setRegenerateMessageId(assistantMessage.id);
  visionAiConversationsStore.addLoadingConversationIds(assistantMessage.conversationId);
  trackAssistantMessageInteraction(VISION_AI_SUMMARY_INTERACTIONS.REGENERATE, message);

  const messagesBeforeRegenerate = [...visionAiMessagesStore.messages];
  let messageCleared = false;

  try {
    if (assistantMessage.intentType && SUMMARY_INTENT_TYPES.includes(assistantMessage.intentType)) {
      await handleRegenerateSummaryMessage(assistantMessage);
    } else {
      visionAiMessagesStore.clearMessageFromHistory(assistantMessage.id);
      messageCleared = true;
      await visionAiMessagesStore.regenerateMessage({
        conversationId: assistantMessage.conversationId,
        messageId: assistantMessage.id,
        messageIntentType: assistantMessage.intentType,
      });
    }
  } catch {
    if (messageCleared) {
      visionAiMessagesStore.setMessages({ newMessages: messagesBeforeRegenerate });
    }
    visionAiConversationsStore.removeLoadingConversationIds(assistantMessage.conversationId);
    addRegenerateErrorToast();
  } finally {
    visionAiMessagesStore.setRegenerateMessageId(null);
  }
}

const headerContextualMenuOptions = computed(() => [
  {
    label: 'New Chat',
    icon: 'message-chat',
    disabled: newChatDisabled.value,
    tooltip: newChatTooltip.value,
    command: () => visionAiStore.startNewChat(),
  },
  {
    label: 'Help',
    icon: 'help-circle',
    command: () => visionAiStore.sendToResourceCenter(),
  },
  {
    label: 'Delete Entire Chat History',
    icon: 'slash-circle',
    class: 'text-danger fill-icon-danger',
    command: () => {
      showClearChatHistoryConfirmation();
    },
  },
]);

function getDateDividerText(index: number): string | null {
  const msgs = messagesWithLoading.value;
  const current = msgs[index];
  if (!current || current.isLoading) return null;
  const currentDate = formatDate(current.timestamp);
  if (index === 0) return currentDate;
  const prev = msgs[index - 1];
  if (prev?.isLoading) return null;
  const previousDate = formatDate(prev.timestamp);
  return currentDate !== previousDate ? currentDate : null;
}

function isLastMessageInConversation(message: ChatMessage) {
  const messages = messagesWithLoading.value;
  const lastMessage = messages[messages.length - 1];
  return message.id === lastMessage?.id;
}

const formattedMessages = computed(() =>
  messagesWithLoading.value.map((msg, index) => ({
    ...msg,
    isAssistant: msg.role === ChatMessageRole.Assistant,
    name: msg.role === ChatMessageRole.User ? userName.value || 'You' : undefined,
    avatarSrc: msg.role === ChatMessageRole.User ? userAvatarUrl.value : undefined,
    formattedTimestamp: msg.isLoading ? undefined : formatTimestamp(msg.timestamp),
    dateDividerText: getDateDividerText(index),
    unanchoredWidgets: unanchoredWidgets(msg),
  })),
);

const cannedPrompts = computed(() => {
  if (stripHtmlTags(promptValue.value).trim()) {
    return [];
  }
  return visionAiStore.sidePanelCannedPrompts;
});

async function toggleChatWindow() {
  if (visible.value && type.value === VISION_AI_PANEL_TYPE) {
    close();
  } else {
    await open(VISION_AI_PANEL_TYPE);
  }
}

onBeforeMount(() => {
  visionAiConversationsStore.getConversations({});
});

onBeforeUnmount(() => {
  if (type.value === VISION_AI_PANEL_TYPE) {
    close();
  }
  // The visible/type watcher is the normal sync path, but a flush: 'pre' watcher
  // queued during teardown may not run before unmount. Force the mirrored store
  // state closed so future task completions can still trigger the Insight pill.
  visionAiStore.setVisionChatPanelOpen(false);
});

function onAssistantMarkdownInteraction(interaction: string, message: ChatMessage) {
  aiMessageInteractionEvent({
    interaction,
    intentType: message.intentType ?? INTENT_TYPES.UNIFIED,
    messageId: message.id,
  });
}
</script>

<template>
  <VisionAiChatButton @toggle-chat-window="toggleChatWindow" />
  <ChatAgentSidePanel
    v-model:prompt-value="promptValue"
    :type="VISION_AI_PANEL_TYPE"
    :messages="formattedMessages"
    :messages-key="loadedConversationId"
    :loading="isLoading"
    :canned-prompts="cannedPrompts"
    :sidebar-min-panel-width="VISION_PANEL_WIDTH_EXPANDED"
    placeholder="Ask Vision AI anything"
    sidebar-toggleable
    sidebar-visible
    :sidebar-storage-key="'vision-chat-sidebar'"
    expandable
    expanded-storage-key="vision-chat"
    default-expanded
    :expanded-override="expandedOverride"
    :expanded-width="VISION_PANEL_WIDTH_EXPANDED"
    @submit="handleSubmit"
    @prompt-insert="handlePromptInsert"
  >
    <template #sidebar>
      <div class="flex min-h-0 w-[240px] flex-1 flex-col">
        <VisionChatHistory />
      </div>
    </template>

    <template #title>
      <VisionAiChip label="Vision AI" />
    </template>

    <template #header-actions>
      <Button
        v-tooltip="newChatTooltip"
        icon="plus"
        size="sm"
        severity="utility"
        aria-label="New chat"
        :disabled="newChatDisabled"
        @click="visionAiStore.startNewChat()"
      />

      <Menu :model="headerContextualMenuOptions" :dropdown="{ placement: 'bottom-end' }">
        <Button icon="dots-vertical" size="sm" severity="utility" aria-label="More options" />
      </Menu>
    </template>

    <template v-if="!visionAiStore.expectingNewMessage" #empty>
      <ChatBubble variant="assistant">
        <template #header>
          <ChatBubbleHeader is-assistant />
        </template>
        <h5 class="typo-h5">
          🚀 Hi! I'm Vision AI - now smarter and ready to help with your social data!
        </h5>
        <p class="typo-base">
          Ask me anything about your social performance or click Summarize on select pages.
        </p>
        <p class="typo-base">
          💡 I can analyze trends, compare metrics, or break down post performance - just tell me
          what you need!
        </p>
      </ChatBubble>
    </template>

    <template #message="{ message }">
      <div v-if="message.dateDividerText" class="relative mt-2 mb-4">
        <div class="h-px bg-white" />
        <div
          class="absolute -top-2 left-1/2 inline-flex h-4 min-w-[120px] -translate-x-1/2 items-center justify-center rounded-[20px] bg-white px-2"
        >
          <span class="text-[10px] font-medium text-stone-500">{{ message.dateDividerText }}</span>
        </div>
      </div>
      <ChatBubble :variant="message.role">
        <template #header>
          <ChatBubbleHeader
            :is-assistant="message.isAssistant"
            :name="message.name"
            :avatar-src="message.avatarSrc"
            :timestamp="message.formattedTimestamp"
          />
        </template>

        <template v-if="message.isLoading">
          <p class="typo-base text-secondary">{{ message.text }}</p>
        </template>

        <template v-else-if="message.role === ChatMessageRole.User">
          <p class="typo-base">{{ message.text }}</p>
        </template>

        <template v-else>
          <AssistantMessageSidePanelContent
            :text="message.text"
            :widgets="message.widgets ?? []"
            class="w-full min-w-0 text-left"
            @message-interaction="onAssistantMarkdownInteraction($event, message)"
          />
          <WidgetWrapper
            v-if="message.unanchoredWidgets.length"
            :widgets="message.unanchoredWidgets"
            :narrow-mode="!isPanelExpanded"
            @expand="handleExpandRequest"
            @widget-interaction="onAssistantMarkdownInteraction($event, message)"
          />
        </template>

        <template v-if="message.isAssistant && !message.isLoading" #footer>
          <ChatBubbleFooter
            show-copy
            :show-regenerate="isLastMessageInConversation(message)"
            show-like-dislike
            :disable-regenerate="isLoading"
            :like-dislike="getLikeDislikeValue(message.feedback)"
            @copy="handleCopyResponse(message)"
            @regenerate="handleRegenerateMessage(message)"
            @update:like-dislike="handleFeedback(message, $event)"
          />
        </template>
      </ChatBubble>
    </template>
  </ChatAgentSidePanel>
</template>
